package models

import kotlin.math.roundToInt

/**
 * Everything the stats screens show about one game, derived from the synced [GameRecapStats] and
 * the live scoreboard. Pure and platform-free so the numbers are unit tested; the Compose layer
 * only lays them out.
 */
data class GameAnalytics(
    val standings: List<StandingRow>,
    /** Cumulative scores by round, or null when the round history can't be trusted (see [historyComplete]). */
    val scoreRace: ScoreRace?,
    val players: List<PlayerAnalytics>,
    val awards: List<GameAward>,
    /** Null until at least one roll with a known total has been recorded. */
    val rollDistribution: RollDistribution?,
    /** Round summaries, newest first. */
    val rounds: List<RoundRecap>,
    val totals: TableTotals,
    /**
     * False when the recorded score changes don't add up to the real scoreboard — e.g. a player on
     * an older app build wrote the recap and dropped the history. Charts built on the history hide
     * themselves instead of telling a wrong story.
     */
    val historyComplete: Boolean,
)

data class StandingRow(
    val playerName: String,
    val points: Int,
    /** Competition ranking: tied scores share a rank (1, 1, 3). */
    val rank: Int,
    val pointsBehindLeader: Int,
    val isBot: Boolean,
)

data class ScoreRace(
    /** X axis: 0 (the start) through the latest round with any recorded play. */
    val roundNums: List<Int>,
    val series: List<ScoreSeries>,
) {
    val maxPoints: Int get() = series.maxOfOrNull { s -> s.points.maxOrNull() ?: 0 } ?: 0
}

data class ScoreSeries(
    val playerName: String,
    /** Table position, which fixes the series color so it never changes with the ranking. */
    val tableIndex: Int,
    /** Cumulative score after each entry of [ScoreRace.roundNums]. */
    val points: List<Int>,
)

data class PlayerAnalytics(
    val playerName: String,
    val tableIndex: Int,
    val points: Int,
    val isBot: Boolean,
    val stats: PlayerGameStats,
) {
    val averageBank: Int? get() = if (stats.banks > 0) stats.pointsBanked / stats.banks else null

    /** Average number of rolls the round had survived when this player banked. */
    val averageRollsBeforeBank: Double? get() =
        if (stats.banks > 0) stats.rollsSurvivedAtBank.toDouble() / stats.banks else null

    val style: PlayStyle? get() = PlayStyle.forAverageRolls(averageRollsBeforeBank)
}

/** A one-word read on how long a player rides before banking. */
enum class PlayStyle {
    CAUTIOUS,
    BALANCED,
    DAREDEVIL;

    companion object {
        // Rolls 1–3 are free (nothing busts), so banking by roll 5 is playing it safe and riding
        // past roll 8 means surviving four-plus live rolls in a row (under a 50% chance).
        const val CAUTIOUS_BELOW = 5.0
        const val DAREDEVIL_ABOVE = 8.0

        fun forAverageRolls(averageRolls: Double?): PlayStyle? = when {
            averageRolls == null -> null
            averageRolls < CAUTIOUS_BELOW -> CAUTIOUS
            averageRolls > DAREDEVIL_ABOVE -> DAREDEVIL
            else -> BALANCED
        }
    }
}

enum class GameAwardKind {
    /** Rode the longest before banking, on average. */
    IRON_NERVES,

    /** Banked earliest, on average — took the sure thing. */
    SAFE_HANDS,

    /** Rolled the most doubles. */
    DOUBLES_DEALER,

    /** Rolled the most busting dice. */
    BUST_MAGNET,

    /** Got caught riding the fewest busts (among players who rode at least one live roll). */
    ESCAPE_ARTIST,
}

data class GameAward(
    val kind: GameAwardKind,
    /** Everyone tied for the award, in table order. */
    val playerNames: List<String>,
    /** The award's headline number: an average roll count ×10, or a plain count. */
    val value: Int,
)

data class RollDistribution(
    /** Counts for totals 2..12, index 0 = total 2. */
    val counts: List<Int>,
) {
    val totalRolls: Int get() = counts.sum()

    fun countFor(total: Int): Int = counts.getOrElse(total - ROLL_TOTAL_RANGE.first) { 0 }

    /** Share of recorded rolls that came up [total], 0..1. */
    fun shareFor(total: Int): Double =
        if (totalRolls == 0) 0.0 else countFor(total).toDouble() / totalRolls

    fun expectedShareFor(total: Int): Double =
        BustOdds.expectedTotalShare.getOrElse(total - ROLL_TOTAL_RANGE.first) { 0.0 }

    val mostCommonTotal: Int? get() {
        val max = counts.maxOrNull() ?: return null
        if (max == 0) return null
        return ROLL_TOTAL_RANGE.first + counts.indexOf(max)
    }
}

data class TableTotals(
    val rolls: Int,
    val sevens: Int,
    val doubles: Int,
    val banks: Int,
    val pointsBanked: Int,
    val busts: Int,
    val roundsCompleted: Int,
    val averagePeakPot: Int?,
)

object GameAnalyticsCalculator {
    fun from(game: DomainGame): GameAnalytics {
        val recap = game.recapStats
        val tablePlayers = game.players.sortedBy { player ->
            game.orderedPlayerNames.indexOf(player.name).let { if (it < 0) Int.MAX_VALUE else it }
        }
        val tableIndexByName = tablePlayers.withIndex().associate { (index, player) -> player.name to index }

        val leaderPoints = tablePlayers.maxOfOrNull { it.points } ?: 0
        val standings = tablePlayers
            .sortedByDescending { it.points }
            .map { player ->
                StandingRow(
                    playerName = player.name,
                    points = player.points,
                    rank = 1 + tablePlayers.count { it.points > player.points },
                    pointsBehindLeader = leaderPoints - player.points,
                    isBot = player.isBot,
                )
            }

        val statsByName = recap.playerStats.associateBy { it.playerName }
        val players = tablePlayers.map { player ->
            PlayerAnalytics(
                playerName = player.name,
                tableIndex = tableIndexByName.getValue(player.name),
                points = player.points,
                isBot = player.isBot,
                stats = statsByName[player.name] ?: PlayerGameStats(playerName = player.name),
            )
        }

        val sortedRounds = recap.rounds.sortedBy { it.roundNum }
        val historyComplete = tablePlayers.all { player ->
            sortedRounds.sumOf { round -> round.pointsFor(player.name) } == player.points
        }
        val scoreRace = if (historyComplete && tablePlayers.isNotEmpty()) {
            buildScoreRace(game, tablePlayers, sortedRounds)
        } else {
            null
        }

        val rollCounts = recap.rollTotals
        val rollDistribution = if (rollCounts.sum() > 0) {
            RollDistribution(counts = List(ROLL_TOTAL_RANGE.count()) { rollCounts.getOrElse(it) { 0 } })
        } else {
            null
        }

        val completedRounds = sortedRounds.filter { it.completed }
        val totals = TableTotals(
            rolls = players.sumOf { it.stats.rolls },
            sevens = players.sumOf { it.stats.sevens },
            doubles = players.sumOf { it.stats.doubles },
            banks = players.sumOf { it.stats.banks },
            pointsBanked = players.sumOf { it.stats.pointsBanked },
            busts = sortedRounds.count { it.busted },
            roundsCompleted = completedRounds.size,
            averagePeakPot = if (completedRounds.isEmpty()) null
            else (completedRounds.sumOf { it.peakPot }.toDouble() / completedRounds.size).roundToInt(),
        )

        return GameAnalytics(
            standings = standings,
            scoreRace = scoreRace,
            players = players,
            awards = awardsFor(players),
            rollDistribution = rollDistribution,
            rounds = sortedRounds.reversed(),
            totals = totals,
            historyComplete = historyComplete,
        )
    }

    private fun buildScoreRace(
        game: DomainGame,
        tablePlayers: List<Player>,
        sortedRounds: List<RoundRecap>,
    ): ScoreRace? {
        val lastRecorded = sortedRounds.maxOfOrNull { it.roundNum } ?: 0
        // While a game is live the current round is shown as it builds; past the end it's capped at
        // the final round so a game-over snapshot never grows a phantom extra round.
        val currentRound = game.round?.roundNum ?: 0
        val cap = if (game.endRoundNum > 0) game.endRoundNum else lastRecorded
        val lastRound = maxOf(lastRecorded, currentRound.coerceAtMost(cap))
        if (lastRound < 1) return null

        val roundNums = (0..lastRound).toList()
        val series = tablePlayers.mapIndexed { index, player ->
            var running = 0
            val points = roundNums.map { roundNum ->
                if (roundNum > 0) {
                    running += sortedRounds.firstOrNull { it.roundNum == roundNum }?.pointsFor(player.name) ?: 0
                }
                running
            }
            ScoreSeries(playerName = player.name, tableIndex = index, points = points)
        }
        return ScoreRace(roundNums = roundNums, series = series)
    }

    private fun awardsFor(players: List<PlayerAnalytics>): List<GameAward> {
        val awards = mutableListOf<GameAward>()
        val bankers = players.filter { it.stats.banks > 0 }
        // Nerve awards compare players against each other, so they need at least two bankers and
        // a real spread — otherwise "Iron Nerves" and "Safe Hands" would name the same person.
        if (bankers.size >= 2) {
            val averages = bankers.associateWith { averageTimesTen(it) }
            val high = averages.values.max()
            val low = averages.values.min()
            if (high > low) {
                awards += GameAward(
                    kind = GameAwardKind.IRON_NERVES,
                    playerNames = bankers.filter { averages.getValue(it) == high }.map { it.playerName },
                    value = high,
                )
                awards += GameAward(
                    kind = GameAwardKind.SAFE_HANDS,
                    playerNames = bankers.filter { averages.getValue(it) == low }.map { it.playerName },
                    value = low,
                )
            }
        }
        leaderAward(players, GameAwardKind.DOUBLES_DEALER) { it.stats.doubles }?.let(awards::add)
        leaderAward(players, GameAwardKind.BUST_MAGNET) { it.stats.bustsRolled }?.let(awards::add)

        val totalBusts = players.maxOfOrNull { it.stats.timesBusted } ?: 0
        if (totalBusts > 0 && players.size >= 2) {
            val fewest = players.minOf { it.stats.timesBusted }
            val escapers = players.filter { it.stats.timesBusted == fewest }
            // Only meaningful when it singles someone out — everyone tied says nothing.
            if (escapers.size < players.size) {
                awards += GameAward(
                    kind = GameAwardKind.ESCAPE_ARTIST,
                    playerNames = escapers.map { it.playerName },
                    value = fewest,
                )
            }
        }
        return awards
    }

    private fun leaderAward(
        players: List<PlayerAnalytics>,
        kind: GameAwardKind,
        selector: (PlayerAnalytics) -> Int,
    ): GameAward? {
        val high = players.maxOfOrNull(selector) ?: return null
        if (high <= 0) return null
        return GameAward(
            kind = kind,
            playerNames = players.filter { selector(it) == high }.map { it.playerName },
            value = high,
        )
    }

    private fun averageTimesTen(player: PlayerAnalytics): Int =
        ((player.averageRollsBeforeBank ?: 0.0) * 10).roundToInt()
}

fun RoundRecap.pointsFor(playerName: String): Int =
    scoreChanges.firstOrNull { it.playerName == playerName }?.points ?: 0
